import { NFSStatus } from "../utils/nfsUtils";
import { LayerFieldsFastSjomerke, LayerFieldsFlytende, LayerFieldsIB, LayerFieldsLys } from "./LayerFields";
import { NauticalClasses } from "./NauticalObjects";
import {
    Beskrivelse,
    Boyeform,
    Eier,
    Fundamenttype,
    Funksjon,
    fyrlistekarakterFastYellowLys,
    JaNei,
    Merkedefinisjon,
    Merkeform,
    Merkemonster,
    Merketypeflytende,
    Navlystype,
    Pillarkategori,
    Radarfyrtype,
    SeamarkColors,
    Sjomerketype,
    Sjostatus,
    Spesialmerketypefast,
    Tilstand,
    Toppmerketype,
    Visuelltydelighet,
} from "./nfsDomains";

const getSeamarkColorInitial = (color: SeamarkColors): string => {
    const colorName = SeamarkColors[color];
    return colorName ? colorName.charAt(0) : "";
};

const rundLysendeSektorTemplate = (color: SeamarkColors) => {
    return {
        LayerName: "Lyssektor",
        FieldValues: [
            {
                FieldName: "lys_farge",
                Value: color,
            },
            {
                FieldName: "sektor_tekst",
                Value: getSeamarkColorInitial(color),
            },
            {
                FieldName: "nfs_status",
                Value: NFSStatus.PROJECTED,
            },
            {
                FieldName: "retning_sektorlinje_1",
                Value: 0,
            },
            {
                FieldName: "retning_sektorlinje_2",
                Value: 360,
            },
            {
                FieldName: "beskrivelse",
                Value: "Rundtlysende",
            },
            {
                FieldName: "sektor_radius",
                Value: 0.025,
            },
            {
                FieldName: "sektor_nr",
                Value: 1,
            },
        ],
    };
};

const genericLightFundament = {
    LayerName: "Fundament",
    FieldValues: [
        { FieldName: "fundament_type", Value: Fundamenttype.Pillar },
        { FieldName: "pillar_kategori", Value: Pillarkategori.PaelePillarStolpe },
        { FieldName: "funksjon", Value: Funksjon.StotteForEtLys },
        { FieldName: "visuell_tydelighet", Value: Visuelltydelighet.TydeligVisuelt },
    ],
};

const lanterneTemplate = (title: string, color: SeamarkColors) => {
    return {
        Title: title,
        LayerName: "Lys",
        FieldValues: [
            {
                FieldName: "klasse",
                Value: NauticalClasses.LA_Lanterne,
            },
            {
                FieldName: "nfs_status",
                Value: NFSStatus.PROJECTED,
            },
            {
                FieldName: "type_id",
                Value: Merkedefinisjon.Lyktefundament,
            },
            {
                FieldName: "lys_farge",
                Value: color,
            },
            {
                FieldName: "lys_type",
                Value: Navlystype.Lanterne,
            },
            {
                FieldName: "navigasjonsinstallasjon_eier",
                Value: Eier.Kystverket,
            },
            {
                FieldName: "s57_status",
                Value: Sjostatus.Permanent,
            },
            {
                FieldName: LayerFieldsLys.nautisk_tilstand,
                Value: Tilstand.God,
            },
            {
                FieldName: LayerFieldsLys.bygg_tilstand,
                Value: Tilstand.God,
            },
        ],
        RelatedLayers: [genericLightFundament, rundLysendeSektorTemplate(color)],
    };
};

const ibTemplate = (title: string, isForHib: boolean, isForFL_LA: boolean) => {
    return {
        Title: title,
        LayerName: "IB",
        FieldValues: [
            {
                FieldName: "nfs_status",
                Value: NFSStatus.PROJECTED,
            },
            {
                FieldName: "klasse",
                Value: NauticalClasses.IB_IndirekteBelysning,
            },
            {
                FieldName: "tilknyttet_hib",
                Value: isForHib ? 1 : 0,
            },
            {
                FieldName: "tilknyttet_fl_la",
                Value: isForFL_LA ? 1 : 0,
            },
            {
                FieldName: "type_id",
                Value: isForHib ? Merkedefinisjon.Lyktefundament : Merkedefinisjon.SpesialFastmerke,
            },
            {
                FieldName: "lys_farge",
                Value: SeamarkColors.Yellow, // IB has always yellow light
            },
            {
                FieldName: "lys_type",
                Value: isForHib ? Navlystype.Lanterne : Navlystype.FlombelysningIndirekteBelysning,
            },
            {
                FieldName: "s57_status",
                Value: Sjostatus.Permanent,
            },
            {
                FieldName: "karakter",
                Value: fyrlistekarakterFastYellowLys,
            },
            {
                FieldName: "navigasjonsinstallasjon_eier",
                Value: Eier.Kystverket,
            },
            {
                FieldName: LayerFieldsIB.bygg_tilstand,
                Value: Tilstand.God,
            },
            {
                FieldName: LayerFieldsIB.nautisk_tilstand,
                Value: Tilstand.God,
            },
        ],
        RelatedLayers: isForHib ? [] : [genericLightFundament],
    };
};

const hibTemplate = (title: string, lysColor: SeamarkColors) => {
    return {
        Title: title,
        LayerName: "Lys",
        FieldValues: [
            {
                FieldName: "nfs_status",
                Value: NFSStatus.PROJECTED,
            },
            {
                FieldName: "klasse",
                Value: NauticalClasses.HIB_HurtigbaatmerkeMedIndirekteBelysning,
            },
            {
                FieldName: "lys_farge",
                Value: lysColor,
            },
            {
                FieldName: "type_id",
                Value: Merkedefinisjon.Lyktefundament,
            },
            {
                FieldName: "lys_type",
                Value: Navlystype.Lanterne,
            },
            {
                FieldName: "s57_status",
                Value: Sjostatus.Permanent,
            },
            {
                FieldName: "navigasjonsinstallasjon_eier",
                Value: Eier.Kystverket,
            },
            {
                FieldName: LayerFieldsLys.bygg_tilstand,
                Value: Tilstand.God,
            },
            {
                FieldName: LayerFieldsLys.nautisk_tilstand,
                Value: Tilstand.God,
            },
        ],
        RelatedLayers: [genericLightFundament, ibTemplate(title, true, false), rundLysendeSektorTemplate(lysColor)],
    };
};

const fyrlyktTemplate = (title: string, withIb: boolean, isFyrstasjon: boolean = false) => {
    return {
        Title: title,
        LayerName: "Fyrlykt",
        FieldValues: [
            {
                FieldName: "nfs_status",
                Value: NFSStatus.PROJECTED,
            },
            {
                FieldName: "klasse",
                Value: isFyrstasjon ? NauticalClasses.FS_Fyrstasjon : NauticalClasses.FL_Fyrlykt,
            },
            {
                FieldName: "lys_type",
                Value: Navlystype.NavigasjonslysGenerelt,
            },
            {
                FieldName: "lys_farge",
                Value: SeamarkColors.Undefined,
            },
            {
                FieldName: "type_id",
                Value: Merkedefinisjon.Lyktefundament,
            },
            {
                FieldName: "s57_status",
                Value: Sjostatus.Permanent,
            },
            {
                FieldName: "navigasjonsinstallasjon_eier",
                Value: Eier.Kystverket,
            },
            {
                FieldName: LayerFieldsLys.bygg_tilstand,
                Value: Tilstand.God,
            },
            {
                FieldName: LayerFieldsLys.nautisk_tilstand,
                Value: Tilstand.God,
            },
        ],
        RelatedLayers: [
            {
                LayerName: "Fundament",
                FieldValues: [
                    { FieldName: "fundament_type", Value: Fundamenttype.Pillar },
                    { FieldName: "pillar_kategori", Value: Pillarkategori.Lyktehus },
                    { FieldName: "funksjon", Value: Funksjon.StotteForEtLys },
                    { FieldName: "visuell_tydelighet", Value: Visuelltydelighet.TydeligVisuelt },
                ],
            },
            ...(withIb ? [ibTemplate(title, false, true)] : []),
        ],
    };
};

const fastmerkeTemplate = (
    title: string,
    nauticalClass: NauticalClasses,
    merkeform: Merkeform,
    typeId: Merkedefinisjon
) => {
    return {
        Title: title,
        LayerName: "Fast sjømerke",
        FieldValues: [
            {
                FieldName: LayerFieldsFastSjomerke.nfs_status,
                Value: NFSStatus.PROJECTED,
            },
            {
                FieldName: LayerFieldsFastSjomerke.merke_form,
                Value: merkeform,
            },
            {
                FieldName: LayerFieldsFastSjomerke.klasse,
                Value: nauticalClass,
            },
            {
                FieldName: LayerFieldsFastSjomerke.type_id,
                Value: typeId,
            },
            {
                FieldName: LayerFieldsFastSjomerke.s57_status,
                Value: Sjostatus.Permanent,
            },
            {
                FieldName: LayerFieldsFastSjomerke.merke_type,
                Value: Spesialmerketypefast.FoulGroundMarkYachting,
            },
            {
                FieldName: LayerFieldsFastSjomerke.navigasjonsinstallasjon_eier,
                Value: Eier.Kystverket,
            },
            {
                FieldName: LayerFieldsFastSjomerke.toppmerke_type,
                Value: Toppmerketype.Viser,
            },
            {
                FieldName: LayerFieldsFastSjomerke.radar_reflektor,
                Value: JaNei.Nei,
            },
        ],
    };
};

const getFlytendeMerkeType = (sjomerketype: Sjomerketype): Merketypeflytende => {
    switch (sjomerketype) {
        case Sjomerketype.Spesialmerke:
        case Sjomerketype.Senterled:
        case Sjomerketype.FrittliggendeGrunneFare:
            return Merketypeflytende.Spesialmerke;
        case Sjomerketype.Nordkardinalmerke:
        case Sjomerketype.BabordLateralmerke:
            return Merketypeflytende.NordkardinalmerkeEllerBabordlateralmerke;
        case Sjomerketype.Ostkardinalmerke:
        case Sjomerketype.StyrbordLateralmerke:
            return Merketypeflytende.OstkardinalmerkeEllerStyrbordlateralmerke;
        case Sjomerketype.Sorkardinalmerke:
            return Merketypeflytende.Sorkardinalmerke;
        case Sjomerketype.Vestkardinalmerke:
            return Merketypeflytende.Vestkardinalmerke;
        default:
            return Merketypeflytende.Udefinert;
    }
};

const getColorsForFlytende = (sjomerketype: Sjomerketype): [SeamarkColors, SeamarkColors, SeamarkColors] => {
    switch (sjomerketype) {
        case Sjomerketype.Nordkardinalmerke:
            return [SeamarkColors.Black, SeamarkColors.Yellow, SeamarkColors.Undefined];
        case Sjomerketype.Sorkardinalmerke:
            return [SeamarkColors.Yellow, SeamarkColors.Black, SeamarkColors.Undefined];
        case Sjomerketype.Ostkardinalmerke:
            return [SeamarkColors.Black, SeamarkColors.Yellow, SeamarkColors.Black];
        case Sjomerketype.Vestkardinalmerke:
            return [SeamarkColors.Yellow, SeamarkColors.Black, SeamarkColors.Yellow];
        case Sjomerketype.BabordLateralmerke:
            return [SeamarkColors.Red, SeamarkColors.Undefined, SeamarkColors.Undefined];
        case Sjomerketype.StyrbordLateralmerke:
            return [SeamarkColors.Green, SeamarkColors.Undefined, SeamarkColors.Undefined];
        case Sjomerketype.Spesialmerke:
            return [SeamarkColors.Yellow, SeamarkColors.Undefined, SeamarkColors.Undefined];
        case Sjomerketype.Senterled:
            return [SeamarkColors.Red, SeamarkColors.White, SeamarkColors.Undefined];
        case Sjomerketype.FrittliggendeGrunneFare:
            return [SeamarkColors.Black, SeamarkColors.Red, SeamarkColors.Black];
        default:
            return [SeamarkColors.Undefined, SeamarkColors.Undefined, SeamarkColors.Undefined];
    }
};

const getFlytendeFargemonster = (sjomerketype: Sjomerketype): Merkemonster => {
    switch (sjomerketype) {
        case Sjomerketype.Nordkardinalmerke:
        case Sjomerketype.Sorkardinalmerke:
        case Sjomerketype.Ostkardinalmerke:
        case Sjomerketype.Vestkardinalmerke:
        case Sjomerketype.FrittliggendeGrunneFare:
            return Merkemonster.VannretteBaand;
        case Sjomerketype.BabordLateralmerke:
        case Sjomerketype.StyrbordLateralmerke:
        case Sjomerketype.Spesialmerke:
            return Merkemonster.Ingen;
        case Sjomerketype.Senterled:
            return Merkemonster.LoddretteStriper;
        default:
            return Merkemonster.Ingen;
    }
};

const flytendeTemplate = (
    title: string,
    sjomerketype: Sjomerketype,
    typeId: Merkedefinisjon,
    id: string | undefined = undefined,
    isLysboye: boolean = false
) => {
    const [color1, color2, color3] = getColorsForFlytende(sjomerketype);

    return {
        Title: title,
        ID: id,
        LayerName: "Flytende merke",
        FieldValues: [
            {
                FieldName: LayerFieldsFlytende.nfs_status,
                Value: NFSStatus.PROJECTED,
            },
            {
                FieldName: LayerFieldsFlytende.sjomerke_type,
                Value: sjomerketype,
            },
            {
                FieldName: LayerFieldsFlytende.merke_type,
                Value: getFlytendeMerkeType(sjomerketype),
            },
            {
                FieldName: LayerFieldsFlytende.klasse,
                Value: NauticalClasses.ST_Stake,
            },
            {
                FieldName: LayerFieldsFlytende.farge_1,
                Value: color1,
            },
            {
                FieldName: LayerFieldsFlytende.farge_2,
                Value: color2,
            },
            {
                FieldName: LayerFieldsFlytende.farge_3,
                Value: color3,
            },
            {
                FieldName: LayerFieldsFlytende.fargemonster,
                Value: getFlytendeFargemonster(sjomerketype),
            },
            {
                FieldName: LayerFieldsFlytende.type_id,
                Value: typeId,
            },
            {
                FieldName: LayerFieldsFlytende.s57_status,
                Value: Sjostatus.Permanent,
            },
            {
                FieldName: LayerFieldsFlytende.tilknyttet_lysboye,
                Value: isLysboye ? 1 : 0,
            },
            {
                FieldName: LayerFieldsFlytende.navigasjonsinstallasjon_eier,
                Value: Eier.Kystverket,
            },
            {
                FieldName: LayerFieldsFlytende.boye_form,
                Value: Boyeform.Stake,
            },
            {
                FieldName: LayerFieldsFlytende.radar_reflektor,
                Value: JaNei.Nei,
            },
        ],
        RelatedLayers: [{ LayerName: "KjettingInfo" }, { LayerName: "LysInfo" }, { LayerName: "StakeInfo" }],
    };
};

const flytendeWithLightTemplate = (title: string, sjomerketype: Sjomerketype, typeId: Merkedefinisjon) => {
    let lysFarge = SeamarkColors.White;
    switch (sjomerketype) {
        case Sjomerketype.BabordLateralmerke:
            lysFarge = SeamarkColors.Red;
            break;
        case Sjomerketype.StyrbordLateralmerke:
            lysFarge = SeamarkColors.Green;
            break;
        case Sjomerketype.Spesialmerke:
            lysFarge = SeamarkColors.Yellow;
    }

    return {
        Title: title,
        LayerName: "Lys",
        FieldValues: [
            {
                FieldName: LayerFieldsLys.nfs_status,
                Value: NFSStatus.PROJECTED,
            },
            {
                FieldName: LayerFieldsLys.klasse,
                Value: NauticalClasses.LB_Lysboeye,
            },
            {
                FieldName: LayerFieldsLys.lys_farge,
                Value: lysFarge,
            },
            {
                FieldName: LayerFieldsLys.type_id,
                Value: typeId,
            },
            {
                FieldName: LayerFieldsLys.lys_type,
                Value: Navlystype.Lanterne,
            },
            {
                FieldName: LayerFieldsLys.navigasjonsinstallasjon_eier,
                Value: Eier.Kystverket,
            },
            {
                FieldName: LayerFieldsLys.s57_status,
                Value: Sjostatus.Permanent,
            },
            {
                FieldName: LayerFieldsLys.beskrivelse,
                Value: Beskrivelse.Lysboye,
            },
            {
                FieldName: LayerFieldsLys.lys_hoyde,
                Value: 2,
            },
        ],
        RelatedLayers: [
            rundLysendeSektorTemplate(lysFarge),
            flytendeTemplate(title, sjomerketype, typeId, undefined, true),
        ],
    };
};

export const NFSLAND_APPCONFIG = {
    MainObjectLayerNames: [
        "Lys",
        "IB",
        "Racon",
        "Fast sjømerke",
        "Flytende merke",
        "Overettlinje lys",
        "Overettlinje merke",
        "Farled lenke",
        "Sikkerhetsareal",
        "Farledsareal",
        "Elektroskap",
        "Sjøkabel",
        "Dybdedata index",
        "Skilt",
    ],
    MainObjectLayerIds: [0, 1, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    Layers: [
        {
            Name: "Lys",
            SearchFields: ["sjomerke_nr", "gis_obj_id", "navn", "sted", "beliggenhet"],
            TitleField: "navn",
            ImportantFields: ["sjomerke_nr", "navn", "sted", "klasse", "type_id"],
            FieldOrder: [
                "sjomerke_nr",
                "lys_type",
                "navn",
                "nfs_status",
                "gis_obj_id",
                "beskrivelse",
                "lampe",
                "karakter",
                "periode",
                "bakgrunnsbelysning",
                "rekkevidde_hvit",
                "rekkevidde_rod",
                "rekkevidde_gronn",
                "rekkevidde_gul",
                "lysstyrke_hvit",
                "lysstyrke_rod",
                "lysstyrke_gronn",
                "lysstyrke_gul",
                "lys_gjennomgang",
                "retningslys_retning",
                "linse_orden",
                "linse_type",
                "klasse",
                "sted",
                "beliggenhet",
                "malt_dato",
                "malt_av",
                "pacc",
                "Lat",
                "Long",
                "kategori",
                "type_id",
                "navigasjonsinstallasjon_eier",
                "opprettet_dato",
                "oppdatert_dato",
                "oppdatert_av",
                "kystverk_region",
                "kystverk_rode",
            ],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "klasse",
                "sjomerke_type",
                "nfs_status",
                "gis_obj_id",
                "lampe",
                "karakter",
                "lys_karakter",
                "periode",
                "lys_farge",
                "bakgrunnsbelysning",
                "lys_gjennomgang",
                "lysstyrke_hvit",
                "rekkevidde_hvit",
                "lysstyrke_rod",
                "rekkevidde_rod",
                "lysstyrke_gronn",
                "rekkevidde_gronn",
                "lysstyrke_gul",
                "rekkevidde_gul",
                "linse_orden",
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
                "kystverk_region",
                "kystverk_rode",
            ],
            zoomScale: 5000,
            LayerId: 0,
            IsTable: false,
            Order: 1,
            DefaultVisible: true,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "IB",
            SearchFields: ["sjomerke_nr", "gis_obj_id", "navn", "sted", "beliggenhet"],
            TitleField: "navn",
            ImportantFields: ["sjomerke_nr", "navn", "sted", "lys_karakter", "periode"],
            FieldOrder: [
                "sjomerke_nr",
                "lys_type",
                "navn",
                "nfs_status",
                "gis_obj_id",
                "beskrivelse",
                "lampe",
                "karakter",
                "periode",
                "bakgrunnsbelysning",
                "rekkevidde_hvit",
                "rekkevidde_rod",
                "rekkevidde_gronn",
                "lysstyrke_hvit",
                "lysstyrke_rod",
                "lysstyrke_gronn",
                "lys_gjennomgang",
                "retningslys_retning",
                "linse_orden",
                "linse_type",
                "klasse",
                "sted",
                "beliggenhet",
                "malt_dato",
                "malt_av",
                "pacc",
                "Lat",
                "Long",
                "kategori",
                "type_id",
                "navigasjonsinstallasjon_eier",
                "opprettet_dato",
                "oppdatert_dato",
                "oppdatert_av",
                "kystverk_region",
                "kystverk_rode",
            ],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "lampe",
                "karakter",
                "periode",
                "lys_farge",
                "nfs_status",
                "bakgrunnsbelysning",
                "lys_gjennomgang",
                "lysstyrke_hvit",
                "rekkevidde_hvit",
                "lysstyrke_rod",
                "rekkevidde_rod",
                "lysstyrke_gronn",
                "rekkevidde_gronn",
                "lysstyrke_gul",
                "rekkevidde_gul",
                "linse_orden",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
                "kystverk_region",
                "kystverk_rode",
            ],
            zoomScale: 5000,
            LayerId: 1,
            IsTable: false,
            Order: 1,
            DefaultVisible: true,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Sektorlinje",
            SearchFields: [],
            TitleField: "navn",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 2,
            IsTable: false,
            Order: 1,
            DefaultVisible: true,
            Type: "FeatureLayer",
            HasPopup: false,
            IsVersioned: true,
            ExcludeFromSearch: true,
            RelationshipChainToMainFeature: ["Lyssektor", "Lys"],
        },
        {
            Name: "Sektorflate",
            SearchFields: [],
            TitleField: "navn",
            ImportantFields: ["lys_farge"],
            FieldOrder: [],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 3,
            IsTable: false,
            Order: 1,
            DefaultVisible: true,
            Type: "FeatureLayer",
            HasPopup: false,
            IsVersioned: true,
            ExcludeFromSearch: true,
            RelationshipChainToMainFeature: ["Lyssektor", "Lys"],
        },
        {
            Name: "Racon",
            SearchFields: ["sjomerke_nr", "gis_obj_id", "navn", "sted", "beliggenhet"],
            TitleField: "navn",
            ImportantFields: ["sjomerke_nr", "navn", "sted", "frekvens_band", "morsetegn", "respons_intervall"],
            FieldOrder: [
                "sjomerke_nr",
                "racon_type",
                "navn",
                "nfs_status",
                "gis_obj_id",
                "beskrivelse",
                "frekvens_band",
                "morsetegn",
                "respons_intervall",
                "racon_hoyde",
                "maksimal_rekkevidde",
                "radar_fyr_type",
                "byggehoyde",
                "klasse",
                "sted",
                "beliggenhet",
                "malt_dato",
                "malt_av",
                "pacc",
                "Lat",
                "Long",
                "kategori",
                "type_id",
                "navigasjonsinstallasjon_eier",
                "opprettet_dato",
                "oppdatert_dato",
                "oppdatert_av",
                "kystverk_region",
                "kystverk_rode",
            ],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "klasse",
                "sjomerke_type",
                "nfs_status",
                "gis_obj_id",
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
                "kystverk_region",
                "kystverk_rode",
            ],
            zoomScale: 5000,
            LayerId: 4,
            IsTable: false,
            Order: 1,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Fast sjømerke",
            SearchFields: ["sjomerke_nr", "gis_obj_id", "navn", "sted", "beliggenhet"],
            TitleField: "navn",
            ImportantFields: ["sjomerke_nr", "navn", "sted", "merke_form", "klasse"],
            FieldOrder: [
                "sjomerke_nr",
                "merke_type",
                "navn",
                "nfs_status",
                "merke_form",
                "gis_obj_id",
                "beskrivelse",
                "byggehoyde",
                "toppmerke_type",
                "refleks_farge",
                "toppmerke_retning",
                "vanndybde",
                "radar_reflektor",
                "klasse",
                "sted",
                "beliggenhet",
                "malt_dato",
                "malt_av",
                "pacc",
                "Lat",
                "Long",
                "kategori",
                "type_id",
                "navigasjonsinstallasjon_eier",
                "opprettet_dato",
                "oppdatert_dato",
                "oppdatert_av",
                "kystverk_region",
                "kystverk_rode",
            ],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "gis_obj_id",
                "merke_form",
                "nfs_status",
                "merke_type",
                "klasse",
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
                "kystverk_region",
                "kystverk_rode",
            ],
            zoomScale: 5000,
            LayerId: 5,
            IsTable: false,
            Order: 1,
            DefaultVisible: true,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Flytende merke",
            SearchFields: ["sjomerke_nr", "gis_obj_id", "navn", "sted", "beliggenhet"],
            TitleField: "navn",
            ImportantFields: ["sjomerke_nr", "navn", "sted", "merke_form"],
            FieldOrder: [
                "sjomerke_nr",
                "merke_type ",
                "navn",
                "nfs_status",
                "gis_obj_id",
                "beskrivelse",
                "boye_form",
                "sjomerke_type",
                "vanndybde",
                "farge_1",
                "farge_2",
                "farge_3",
                "fargemonster",
                "radar_reflektor",
                "klasse",
                "sted",
                "beliggenhet",
                "malt_dato",
                "malt_av",
                "pacc",
                "Lat",
                "Long",
                "kategori",
                "type_id",
                "navigasjonsinstallasjon_eier",
                "opprettet_dato",
                "oppdatert_dato",
                "oppdatert_av",
                "kystverk_region",
                "kystverk_rode",
            ],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "sjomerke_type",
                "nfs_status",
                "gis_obj_id",
                "merke_type",
                "klasse",
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
                "kystverk_region",
                "kystverk_rode",
                "farge_1",
                "farge_2",
                "farge_3",
            ],
            zoomScale: 5000,
            LayerId: 6,
            IsTable: false,
            Order: 1,
            DefaultVisible: true,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Overettlinje lys",
            SearchFields: ["gis_obj_id", "beskrivelse"],
            TitleField: "gis_obj_id",
            ImportantFields: ["retning_verdi", "klasse"],
            FieldOrder: [],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 7,
            IsTable: false,
            Order: 1,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Overettlinje merke",
            SearchFields: ["gis_obj_id", "beskrivelse"],
            TitleField: "gis_obj_id",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 8,
            IsTable: false,
            Order: 1,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Farled lenke",
            SearchFields: ["gis_obj_id", "farled_navn", "farled_nr"],
            TitleField: "farled_navn",
            ImportantFields: ["farled_navn"],
            FieldOrder: [],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 9,
            IsTable: false,
            Order: 1,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: true,
        },
        {
            Name: "Sikkerhetsareal",
            SearchFields: ["gis_obj_id", "farled_navn", "farled_nr"],
            TitleField: "farled_navn",
            ImportantFields: ["farled_navn"],
            FieldOrder: [],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 10,
            IsTable: false,
            Order: 1,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Farledsareal",
            SearchFields: ["gis_obj_id", "farled_navn", "farled_nr"],
            TitleField: "farled_navn",
            ImportantFields: ["farled_navn"],
            FieldOrder: [],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 11,
            IsTable: false,
            Order: 1,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Elektroskap",
            SearchFields: ["navn", "beskrivelse"],
            TitleField: "navn",
            ImportantFields: ["elektroskap_type"],
            FieldOrder: [],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "nfs_status",
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 12,
            IsTable: false,
            Order: 1,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Sjøkabel",
            SearchFields: [],
            TitleField: "",
            ImportantFields: ["fraelektroskap", "kabel_type"],
            FieldOrder: [],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 13,
            IsTable: false,
            Order: 1,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Dybdedata index",
            SearchFields: [],
            TitleField: "",
            ImportantFields: ["dybde_malemetode"],
            FieldOrder: [],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 14,
            IsTable: false,
            Order: 0,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Skilt",
            SearchFields: ["sjomerke_nr", "gis_obj_id", "sted", "beliggenhet"],
            TitleField: "",
            ImportantFields: ["sted", "beliggenhet", "skilt_type", "skilt_tekst", "underskilttekst"],
            FieldOrder: [],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
                "kystverk_region",
                "kystverk_rode",
            ],
            zoomScale: 5000,
            LayerId: 15,
            IsTable: false,
            Order: 1,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: true,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Fundament",
            SearchFields: [],
            TitleField: "fundament_type",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "globalid",
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "fk_lys",
                "fk_ib",
                "fk_flytendemerke",
                "fk_racon",
                "fk_fastsjomerke",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 16,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
        },
        {
            Name: "KjettingInfo",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 17,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
        },
        {
            Name: "LysInfo",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 18,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
        },
        {
            Name: "Lyssektor",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
                "nfs_status",
            ],
            zoomScale: 5000,
            LayerId: 19,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
        },
        {
            Name: "Skjerming",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 20,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
        },
        {
            Name: "StakeInfo",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 21,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
        },
        {
            Name: "Candela_CL",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 22,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
        },
        {
            Name: "Karakter_CL",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 23,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
        },
        {
            Name: "Versjonstilgang",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 24,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: false,
        },

        {
            Name: "Farled_FastSjomerke",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 25,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
            RelationshipDetails: {
                relation1LayerId: 9,
                relation2LayerId: 5,
                relation1FieldName: "farled_guid",
                relation2FieldName: "fastsjomerke_guid",
            },
        },
        {
            Name: "Farled_FlytendeMerke",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 26,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
            RelationshipDetails: {
                relation1LayerId: 9,
                relation2LayerId: 6,
                relation1FieldName: "farled_guid",
                relation2FieldName: "flytendemerke_guid",
            },
        },
        {
            Name: "Farled_IB",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 27,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
            RelationshipDetails: {
                relation1LayerId: 9,
                relation2LayerId: 1,
                relation1FieldName: "farled_guid",
                relation2FieldName: "ib_guid",
            },
        },
        {
            Name: "Farled_Lys",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 28,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
            RelationshipDetails: {
                relation1LayerId: 9,
                relation2LayerId: 0,
                relation1FieldName: "farled_guid",
                relation2FieldName: "lys_guid",
            },
        },
        {
            Name: "Farled_Racon",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 29,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
            RelationshipDetails: {
                relation1LayerId: 9,
                relation2LayerId: 4,
                relation1FieldName: "farled_guid",
                relation2FieldName: "racon_guid",
            },
        },
        {
            Name: "Lys_FlytendeMerke",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 30,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
            RelationshipDetails: {
                relation1LayerId: 0,
                relation2LayerId: 6,
                relation1FieldName: "lys_guid",
                relation2FieldName: "flytendemerke_guid",
            },
        },
        {
            Name: "Lys_IB",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 31,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
            RelationshipDetails: {
                relation1LayerId: 0,
                relation2LayerId: 1,
                relation1FieldName: "lys_guid",
                relation2FieldName: "ib_guid",
            },
        },
        {
            Name: "Lys_Racon",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [],
            StatusField: "",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "malt_av",
                "malt_dato",
                "pacc",
                "latitude",
                "longitude",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "malt_av",
                "malt_dato",
            ],
            zoomScale: 5000,
            LayerId: 32,
            IsTable: true,
            Type: "FeatureLayer",
            IsVersioned: true,
            RelationshipDetails: {
                relation1LayerId: 0,
                relation2LayerId: 4,
                relation1FieldName: "lys_guid",
                relation2FieldName: "racon_guid",
            },
        },
        {
            Name: "Flytende merke Innmålt",
            SearchFields: [],
            TitleField: "malt_dato",
            ImportantFields: ["sjomerke_nr", "navn", "sted", "merke_form"],
            FieldOrder: [
                "objectid",
                "sjomerke_nr",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "malt_av",
                "malt_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "fk_flytendemerke",
                "globalid",
                "sjomerke_type",
            ],
            StatusField: "nfs_status",
            ExcludedFields: [
                "gyldig_fra",
                "gyldig_til",
                "efs_melding",
                "efs_sendt_av",
                "s57_status",
                "objectid",
                "globalid",
            ],
            ReadonlyFields: [
                "objectid",
                "sjomerke_nr",
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "malt_av",
                "malt_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
                "fk_flytendemerke",
                "globalid",
                "sjomerke_type",
            ],
            zoomScale: 5000,
            LayerId: 34,
            IsTable: false,
            Order: 1,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: false,
            IsVersioned: true,
            ExcludeFromSearch: true,
        },
        {
            Name: "Hjelpepunkt",
            SearchFields: [],
            TitleField: "",
            ImportantFields: [],
            FieldOrder: [
                "tilknyttet_sjomerke_nr",
                "beskrivelse",
                "malt_av",
                "malt_dato",
                "opprettet_av",
                "opprettet_dato",
            ],
            StatusField: null,
            ExcludedFields: ["objectid", "globalid"],
            ReadonlyFields: [
                "opprettet_av",
                "opprettet_dato",
                "oppdatert_av",
                "oppdatert_dato",
                "malt_dato",
                "malt_av",
            ],
            zoomScale: 5000,
            LayerId: 35,
            IsTable: false,
            Order: 1,
            DefaultVisible: false,
            Type: "FeatureLayer",
            HasPopup: false,
            IsVersioned: true,
            ExcludeFromSearch: false,
        },
        {
            Name: "Flytende merke Tildelt",
            SearchFields: [],
            TitleField: "sjomerke_nr",
            ImportantFields: ["sjomerke_nr"],
            FieldOrder: [],
            StatusField: null,
            ExcludedFields: ["globalid"],
            ReadonlyFields: [],
            zoomScale: 1000,
            LayerId: 37,
            IsTable: false,
            Order: 1,
            DefaultVisible: true,
            Type: "FeatureLayer",
            HasPopup: false,
            IsVersioned: true,
            ExcludeFromSearch: true,
        },
    ],
    Basemaps: [
        {
            Name: "Default",
            ServiceUrl:
                "https://services.geodataonline.no/arcgis/rest/services/GeocacheVector/GeocacheBasis_WGS84/VectorTileServer",
            ThumbnailUri: "pack://application:,,,/NFS.Boat;component/Images/thumbnail-GeocacheBasis.png",
        },
        {
            Name: "Kyst vector",
            ServiceUrl:
                "https://services.geodataonline.no/arcgis/rest/services/GeocacheVector/GeocacheKyst/VectorTileServer",
            ThumbnailUri: "pack://application:,,,/NFS.Boat;component/Images/thumbnail-GeocacheKyst.png",
        },
        {
            Name: "Kyst UTM33",
            ServiceUrl:
                "https://services.geodataonline.no/arcgis/rest/services/Geocache_UTM33_EUREF89/GeocacheKyst/MapServer",
            ThumbnailUri: "pack://application:,,,/NFS.Boat;component/Images/thumbnail-Geocache_UTM33_EUREF89.png",
        },
        {
            Name: "Bilder",
            ServiceUrl:
                "https://services.geodataonline.no/arcgis/rest/services/Geocache_UTM33_EUREF89/GeocacheBilder/MapServer",
            ThumbnailUri: "pack://application:,,,/NFS.Boat;component/Images/thumbnail-GeocacheBilder.png",
        },
        {
            Name: "Basis",
            ServiceUrl:
                "https://services.geodataonline.no/arcgis/rest/services/GeocacheVector/GeocacheBasis/VectorTileServer",
            ThumbnailUri: "pack://application:,,,/NFS.Boat;component/Images/thumbnail-GeocacheBasis.png",
        },
    ],
    HeightmapUrl:
        "https://services.geodataonline.no/arcgis/rest/services/Geocache_UTM33_EUREF89/GeocacheTerreng/ImageServer",
    Scale: {
        Scales: [1000, 10000, 100000],
        DefaultScale: 10000,
        MinScale: 10000000,
        MaxScale: 1,
    },
    Projection: {
        CenterMeridian: 15, // This depends on the UTM zone used! http://www.jaworski.ca/utmzones.htm
    },
    CreateObject: [
        {
            GroupName: "Fyrlykt",
            GroupEntries: [fyrlyktTemplate("Fyrlykt", false), fyrlyktTemplate("Fyrlykt med IB", true)],
        },
        {
            GroupName: "Lys",
            GroupEntries: [
                lanterneTemplate("Lanterne, rød", SeamarkColors.Red),
                lanterneTemplate("Lanterne, grønn", SeamarkColors.Green),
                lanterneTemplate("Lanterne, gul", SeamarkColors.Yellow),
                lanterneTemplate("Lanterne, hvit", SeamarkColors.White),
                hibTemplate("HIB, rød", SeamarkColors.Red),
                hibTemplate("HIB, grønn", SeamarkColors.Green),
                hibTemplate("HIB, hvit", SeamarkColors.White),
                fyrlyktTemplate("Fyrstasjon", false, true),
                flytendeWithLightTemplate(
                    "Installasjonsbøye",
                    Sjomerketype.Spesialmerke,
                    Merkedefinisjon.Installasjonsboye
                ),
                flytendeWithLightTemplate(
                    "Frittliggende grunnefare m/lys",
                    Sjomerketype.FrittliggendeGrunneFare,
                    Merkedefinisjon.IsolertFareboye
                ),
                flytendeWithLightTemplate("Senterledbøye m/lys", Sjomerketype.Senterled, Merkedefinisjon.Senterledboye),
                flytendeWithLightTemplate(
                    "Kardinalbøye nord m/lys",
                    Sjomerketype.Nordkardinalmerke,
                    Merkedefinisjon.Nordkardinalboye
                ),
                flytendeWithLightTemplate(
                    "Kardinalbøye øst m/lys",
                    Sjomerketype.Ostkardinalmerke,
                    Merkedefinisjon.Ostkardinalboye
                ),
                flytendeWithLightTemplate(
                    "Kardinalbøye sør m/lys",
                    Sjomerketype.Sorkardinalmerke,
                    Merkedefinisjon.Sorkardinalboye
                ),
                flytendeWithLightTemplate(
                    "Kardinalbøye vest m/lys",
                    Sjomerketype.Vestkardinalmerke,
                    Merkedefinisjon.Vestkardinalboye
                ),
                flytendeWithLightTemplate(
                    "Lateralbøye styrbord m/lys",
                    Sjomerketype.StyrbordLateralmerke,
                    Merkedefinisjon.StyrbordLateralboye
                ),
                flytendeWithLightTemplate(
                    "Lateralbøye babord m/lys",
                    Sjomerketype.BabordLateralmerke,
                    Merkedefinisjon.BabordLateralboye
                ),
                flytendeWithLightTemplate("Spesialmerke m/lys", Sjomerketype.Spesialmerke, Merkedefinisjon.Spesialboye),
            ],
        },
        {
            GroupName: "Racon",
            GroupEntries: [
                {
                    Title: "Racon",
                    LayerName: "Racon",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        {
                            FieldName: "klasse",
                            Value: NauticalClasses.RA_Racon,
                        },
                        {
                            FieldName: "radar_fyr_type",
                            Value: Radarfyrtype.RadarSvarfyr,
                        },
                        {
                            FieldName: "navigasjonsinstallasjon_eier",
                            Value: Eier.Kystverket,
                        },
                    ],
                },
            ],
        },
        {
            GroupName: "IB",
            GroupEntries: [ibTemplate("IB", false, false)],
        },
        {
            GroupName: "Fast sjømerke",
            GroupEntries: [
                fastmerkeTemplate(
                    "Varde",
                    NauticalClasses.VA_Varde,
                    Merkeform.Varde,
                    Merkedefinisjon.IsolertFareFastmerke
                ),
                fastmerkeTemplate(
                    "Båke",
                    NauticalClasses.BK_Baake,
                    Merkeform.Baake,
                    Merkedefinisjon.IsolertFareFastmerke
                ),
                fastmerkeTemplate(
                    "Stang, påle",
                    NauticalClasses.SG_Stang,
                    Merkeform.StangPaale,
                    Merkedefinisjon.IsolertFareFastmerke
                ),
            ],
        },
        {
            GroupName: "Flytende merke",
            GroupEntries: [
                flytendeTemplate(
                    "Frittliggende Grunnefare",
                    Sjomerketype.FrittliggendeGrunneFare,
                    Merkedefinisjon.IsolertFareboye,
                    "frittliggende_grunnefare"
                ),
                flytendeTemplate("Senterled", Sjomerketype.Senterled, Merkedefinisjon.Senterledboye),
                flytendeTemplate(
                    "Kardinalmerke Nord",
                    Sjomerketype.Nordkardinalmerke,
                    Merkedefinisjon.Nordkardinalboye
                ),
                flytendeTemplate("Kardinalmerke Øst", Sjomerketype.Ostkardinalmerke, Merkedefinisjon.Ostkardinalboye),
                flytendeTemplate("Kardinalmerke Sør", Sjomerketype.Sorkardinalmerke, Merkedefinisjon.Sorkardinalboye),
                flytendeTemplate(
                    "Kardinalmerke Vest",
                    Sjomerketype.Vestkardinalmerke,
                    Merkedefinisjon.Vestkardinalboye
                ),
                flytendeTemplate(
                    "Lateralmerke Babord",
                    Sjomerketype.BabordLateralmerke,
                    Merkedefinisjon.BabordLateralboye
                ),
                flytendeTemplate(
                    "Lateralmerke Styrbord",
                    Sjomerketype.StyrbordLateralmerke,
                    Merkedefinisjon.StyrbordLateralboye
                ),
                flytendeTemplate("Spesialmerke", Sjomerketype.Spesialmerke, Merkedefinisjon.Spesialboye),
            ],
        },
        {
            GroupName: "Overett",
            GroupEntries: [
                {
                    Title: "Overett",
                    LayerName: "Overettlinje lys",
                    FieldValues: [
                        { FieldName: "klasse", Value: 11 },
                        {
                            FieldName: "navigasjonsinstallasjon_eier",
                            Value: Eier.Kystverket,
                        },
                    ],
                },
            ],
        },
        {
            GroupName: "Sjøkabel",
            GroupEntries: [
                {
                    Title: "Sjøkabel",
                    LayerName: "Sjøkabel",
                },
            ],
        },
        // Never heard anything from Kysverket about creating these. Removing for now. They also don't have NFS status field.
        // {
        //     GroupName: "Farled lenke",
        //     GroupEntries: [
        //         {
        //             Title: "Hovedled",
        //             LayerName: "Farled lenke",
        //             FieldValues: [
        //                 {
        //                     FieldName: "nfs_status",
        //                     Value: NFSStatus.PROJECTED,
        //                 },
        //                 { FieldName: "farled_kategori", Value: 1 },
        //             ],
        //         },
        //         {
        //             Title: "Biled",
        //             LayerName: "Farled lenke",
        //             FieldValues: [
        //                 {
        //                     FieldName: "nfs_status",
        //                     Value: NFSStatus.PROJECTED,
        //                 },
        //                 { FieldName: "farled_kategori", Value: 2 },
        //             ],
        //         },
        //     ],
        // },
        // Never heard anything from Kysverket about creating these. Removing for now. They also don't have NFS status field.
        // {
        //     GroupName: "Sikkerhetsareal",
        //     GroupEntries: [
        //         {
        //             Title: "Sikkerhetsareal",
        //             LayerName: "Sikkerhetsareal",
        //             FieldValues: [
        //                 {
        //                     FieldName: "nfs_status",
        //                     Value: NFSStatus.PROJECTED,
        //                 },
        //             ],
        //         },
        //     ],
        // },
        // Never heard anything from Kysverket about creating these. Removing for now. They also don't have NFS status field.
        // {
        //     GroupName: "Farledsareal",
        //     GroupEntries: [
        //         {
        //             Title: "Farledsareal",
        //             LayerName: "Farledsareal",
        //             FieldValues: [
        //                 {
        //                     FieldName: "nfs_status",
        //                     Value: NFSStatus.PROJECTED,
        //                 },
        //             ],
        //         },
        //     ],
        // },
        {
            GroupName: "Elektroskap",
            GroupEntries: [
                {
                    Title: "Elektroskap",
                    LayerName: "Elektroskap",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                    ],
                },
            ],
        },
        // Never heard anything from Kysverket about creating these. Removing for now. They also don't have NFS status field.
        // {
        //     GroupName: "Dybdedata index",
        //     GroupEntries: [
        //         {
        //             Title: "Planlagt kartlagt",
        //             LayerName: "Dybdedata index",
        //             FieldValues: [{ FieldName: "nfs_status", Value: NFSStatus.PROJECTED }],
        //         },
        //         {
        //             Title: "Påbegynt kartlagt",
        //             LayerName: "Dybdedata index",
        //             FieldValues: [{ FieldName: "nfs_status", Value: 3 }],
        //         },
        //         {
        //             Title: "Ferdig kartlagt",
        //             LayerName: "Dybdedata index",
        //             FieldValues: [{ FieldName: "nfs_status", Value: 1 }],
        //         },
        //     ],
        // },
        {
            GroupName: "Skilt",
            GroupEntries: [
                {
                    Title: "Ankring forbudt",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 8 },
                        { FieldName: "klasse", Value: 2 },
                    ],
                },
                {
                    Title: "Annen fare",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 6 },
                        { FieldName: "klasse", Value: 1 },
                    ],
                },
                {
                    Title: "Anropskanal VHF",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 13 },
                    ],
                },
                {
                    Title: "Beste punkt for passering",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 26 },
                        { FieldName: "klasse", Value: 3 },
                    ],
                },
                {
                    Title: "Bevegelig bru",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 5 },
                        { FieldName: "klasse", Value: 1 },
                    ],
                },
                {
                    Title: "Dato",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 19 },
                        { FieldName: "klasse", Value: 4 },
                    ],
                },
                {
                    Title: "Dykking forbudt",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 7 },
                        { FieldName: "klasse", Value: 2 },
                    ],
                },
                {
                    Title: "Fartsgrense",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 10 },
                        { FieldName: "klasse", Value: 2 },
                    ],
                },
                {
                    Title: "Fartøy større enn... BT",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 20 },
                        { FieldName: "klasse", Value: 4 },
                    ],
                },
                {
                    Title: "Horisontal klaring",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 4 },
                        { FieldName: "klasse", Value: 1 },
                    ],
                },
                {
                    Title: "Kabel, rørledning, vannledning, avløpsledning",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 21 },
                        { FieldName: "klasse", Value: 4 },
                    ],
                },
                {
                    Title: "Kabelferge",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 1 },
                        { FieldName: "klasse", Value: 1 },
                    ],
                },
                {
                    Title: "Lav bru",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 22 },
                        { FieldName: "klasse", Value: 4 },
                    ],
                },
                {
                    Title: "Livsfarlig ledning",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 2 },
                        { FieldName: "klasse", Value: 1 },
                    ],
                },
                {
                    Title: "Opphør fartsgrense",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 11 },
                        { FieldName: "klasse", Value: 2 },
                    ],
                },
                {
                    Title: "Overett",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 27 },
                        { FieldName: "klasse", Value: 5 },
                    ],
                },
                {
                    Title: "Sakte fart",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 12 },
                        { FieldName: "klasse", Value: 3 },
                    ],
                },
                {
                    Title: "Sidemarkering babord",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 25 },
                        { FieldName: "klasse", Value: 5 },
                    ],
                },
                {
                    Title: "Sidemarkering styrbord",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 24 },
                        { FieldName: "klasse", Value: 5 },
                    ],
                },
                {
                    Title: "Sjøtrafikk forbudt",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 9 },
                        { FieldName: "klasse", Value: 2 },
                    ],
                },
                {
                    Title: "Strekningens lengde bortenfor skiltet i meter",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 14 },
                        { FieldName: "klasse", Value: 4 },
                    ],
                },
                {
                    Title: "Strekningens lengde bortenfor skiltet i nautiske mil",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 15 },
                        { FieldName: "klasse", Value: 4 },
                    ],
                },
                {
                    Title: "Tid",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 18 },
                        { FieldName: "klasse", Value: 4 },
                    ],
                },
                {
                    Title: "Vertikal klaring",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 3 },
                        { FieldName: "klasse", Value: 1 },
                    ],
                },
                {
                    Title: "Virkeområde",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 23 },
                        { FieldName: "klasse", Value: 4 },
                    ],
                },
                {
                    Title: "Annet",
                    LayerName: "Skilt",
                    FieldValues: [
                        {
                            FieldName: "nfs_status",
                            Value: NFSStatus.PROJECTED,
                        },
                        { FieldName: "skilt_type", Value: 0 },
                        { FieldName: "klasse", Value: 0 },
                    ],
                },
            ],
        },
        {
            GroupName: "Hjelpepunkt",
            GroupEntries: [
                {
                    Title: "Hjelpepunkt",
                    LayerName: "Hjelpepunkt",
                },
            ],
        },
    ],
    Lighthouse: {
        DefaultSectors: [
            { index: 0, color: "4", startAngle: 0 }, // green
            { index: 1, color: "1", startAngle: 60 }, // yellow
            { index: 2, color: "3", startAngle: 120 }, // red
            { index: 3, color: "4", startAngle: 180 }, // green
            { index: 4, color: "1", startAngle: 240 }, // yellow
            { index: 5, color: "3", startAngle: 300 }, // red
        ],
        MinSectorLineLength: 92.6, // Meters (0.05 Nm)
        MaxSectorLineLength: 92600, // Meters (50 Nm)
        MinSectorThickness: 20,
        MaxSectorThickness: 60,
        AdaptiveSectorThickness: true,
        DefaultSectorThickness: 60,
    },
    MaxCatalogItem: 100,
    DiscontinuedOpacity: 0.3,
    EFSLayerNames: ["Lys", "IB", "Racon", "Fast sjømerke", "Flytende merke", "Skilt"],
    JobsConfig: {
        MeasurementPointsLayerId: 0,
        JobsLayerId: 1,
        StepsLayerId: 2,
        AttachmentsLayerId: 3,
        MeasurementsDocumentationLayerId: 4,
        RelatedObjectsLayerId: 5,
        WorkOrderLayerId: 6,
    },
    FeatureEditor: {
        SecondaryFeatureCopyAttributeConfig: {
            AllSecondary: [
                "beliggenhet",
                "navn",
                "bygg_tilstand",
                "kategori",
                "kystverk_region",
                "kystverk_rode",
                "nautisk_tilstand",
                "nautisk_funksjon",
                "navigasjonsinstallasjon_eier",
                "pacc",
                "sjomerke_nr",
                "sksd_status",
                "sted",
                "malt_av",
                "malt_dato",
                "nedlagt_av",
                "nedlagt_dato",
                "fjernet_av",
                "fjernet_dato",
            ],
            IBSecondary: [LayerFieldsIB.lys_hoyde, LayerFieldsIB.beskrivelse, LayerFieldsIB.byggehoyde],
            // Not in AllSecondary: an IB under a Fyrlykt/Lanterne has a different type_id than its primary.
            // retningslys_retning is hidden on a Fyrlykt/Lanterne, so its IB owns the value instead.
            HibLysboyeSecondary: [LayerFieldsLys.type_id, LayerFieldsIB.retningslys_retning],
        },
        ReadonlyFields: [
            "nfs_status",
            "sksd_status",
            "tilknyttet_lysboye",
            "tilknyttet_hib",
            "tilknyttet_fl_la",
            "klasse",
            "merke_type",
            "sjomerke_type ",
            "nedlagt_av",
            "nedlagt_dato",
            "fjernet_av",
            "fjernet_dato",
            "malt_av",
            "malt_dato",
            "pacc",
            "objectid",
            "gis_obj_id",
            "Lat",
            "Long",
            "gyldig_fra",
            "gyldig_til",
        ],
        ExcludedFields: [
            "navigasjonsinstallasjon_eier_h",
            "type_id_h",
            "beskrivelse_h",
            "karakter_h",
            "periode_h",
            "bygg_tilstand",
            "nautisk_tilstand",
            "nautisk_funksjon",
            "siste_kontrollskjerming_dato",
        ],
    },
};
